import { subscribe, emit, events } from './eventManager.js';
import { fetchCountry, fetchCountries } from './countryUtils.js';
import { COOKIE_DEFAULT_PATH, COOKIE_MAX_AGE, COOKIE_SAMESITE_STRICT, getCookieDomain, getCookieValue, setCookie } from './utils/cookies.js';
import { DEV_ENV, PROD_ENV, UAT_ENV } from './constants/environments.js';
import logger from './logger.js';
/**
 * Name of the cookie used to store the users geolocated country (which is overridden manually in the country selector).
 */
const COUNTRY_STORAGE_KEY = 'SelectedCountry';
/**
 * Host domain name used to write the cookies so that they are environment specific (stage.sw.siemens.com, sw.siemens.com).
 */
let countryCookieDomain;
/**
 * Default country initially set to ensure a country is selected.
 */
const DEFAULT_COUNTRY = { countryname: 'United States', countrycode: 'US', default: true };
/**
 * Tracks the active country for the module.
 */
let country = DEFAULT_COUNTRY;
/**
 * Tracks the current API environment from which to make queries. Updated via disw.init({..}).
 */
let countryEnv = PROD_ENV;
/**
 * Initializes the country setting based on user authentication status and preferences.
 *
 * This function determines the user's country in the following priority order:
 * 1. For authenticated users: country from Siemens ID profile
 * 2. Previously stored country preference in cookies
 * 3. Geolocation-based country detection
 * 4. Default country (fallback)
 *
 * @param {CountryProps} config - Configuration options
 * @param {string} [config.env=PROD_ENV] - Environment to use for API calls
 * @returns {Promise<void>} A promise that resolves when the country has been initialized
 *
 * @example
 *     await initializeCountry({ env: DEV_ENV });
 */
export async function initializeCountry(config = {}) {
    // Set up environment configuration
    const { env = PROD_ENV } = config;
    countryEnv = env;
    countryCookieDomain = getCookieDomain([DEV_ENV, UAT_ENV].includes(countryEnv));
    // Get stored country preference and check authentication status
    const storedCountry = _getLocally();
    const authenticated = window?.disw?.account?.getAccount()?.authenticated;
    if (authenticated) {
        // For authenticated users, setCountry will retrieve country from the user's account info so sending blank values.
        await setCountry({ countrycode: '', countryname: '' });
        return;
    }
    // For non-authenticated users with stored preference
    if (storedCountry) {
        country = await getFullCountryName(storedCountry?.countrycode);
        _storeLocally(country);
        emit(events.COUNTRY, country, false);
        return;
    }
    // No stored preference, attempt to geolocate
    try {
        const { countryCode, countryName } = await fetchCountry(countryEnv);
        await setCountry(await getFullCountryName(countryCode));
    }
    catch (error) {
        logger.error(`Failed to fetch country: ${error}`);
        await setCountry(country); // fallback to default
    }
}
/**
 * Retrieves the full country name for a given country code.
 *
 * This function looks up the country name from the list of all countries
 * using the provided ISO country code.
 *
 * @param {string} countryCode - The ISO 3166-1 alpha-2 country code (e.g., 'US', 'DE')
 * @returns {Promise<Country>} A promise that resolves to a Country object
 * @throws {Error} If the country code is not found in the available countries list
 *
 * @example
 *     const country = await getFullCountryName('US');
 *     // Returns { countrycode: 'US', countryname: 'United States' }
 */
export async function getFullCountryName(countryCode) {
    // Fetch all countries using the English locale
    const allCountries = await getAllCountries(window?.disw?.locales?.getCurrentLocaleCode() || 'en-US');
    // Find the matching country
    const matched = allCountries?.countries?.find((c) => c.value === countryCode);
    // Handle case when country is not found
    if (!matched?.label) {
        const errorMessage = `Country with code ${countryCode} not found in the list.`;
        logger.error(errorMessage);
        throw new Error(errorMessage);
    }
    return {
        countrycode: countryCode,
        countryname: matched.label,
        default: false
    };
}
/**
 * This is a function to pull the list of countries from Contentful. Cache the country list per locale
 *
 * @returns - list of countries in an array with a label (i.e., Argentina) and a 2-digit country code (i.e., AR)
 *
 * @example
 *      getAllCountries('en-US')
 */
async function getAllCountries(localeCode) {
    const sessionStorageKey = `countries_${localeCode}`;
    try {
        const cachedCountriesList = window.sessionStorage.getItem(sessionStorageKey);
        if (cachedCountriesList) {
            return JSON.parse(cachedCountriesList);
        }
        // Pull the list of countries and cache in session storage
        const countryList = await _fetchCountriesWithRetries(localeCode, countryEnv, 1);
        window.sessionStorage.setItem(sessionStorageKey, JSON.stringify(countryList));
        return countryList;
    }
    catch (error) {
        logger.error(error);
    }
}
/**
 * Pull the list of countries from Contentful. Reload the country list if it fails in first attempt.
 *
 * @param {string} localeCode
 * @param {string} countryEnv
 * @param {number} retries
 *
 * @returns list of countries in an array with a label.
 */
async function _fetchCountriesWithRetries(localeCode, countryEnv, retries) {
    let countries;
    try {
        countries = await fetchCountries(localeCode, countryEnv);
        return countries;
    }
    catch (error) {
        if (retries > 0) {
            return _fetchCountriesWithRetries(localeCode, countryEnv, retries - 1);
        }
        logger.error(`Error loading country data: ${error} with retries '${retries}'`);
        throw error;
    }
}
/**
 * Sets the active country for the user session.
 *
 * This function determines and sets the country based on the following logic:
 * 1. For authenticated users: Uses the country from their Siemens ID profile
 * 2. For non-authenticated users: Sets the provided country
 *
 * The function stores the country in cookies and emits a country change event.
 *
 * @param {Country} newCountry - The country object to set (used for non-authenticated users)
 * @returns {Promise<void>} A promise that resolves when the country has been set
 *
 * @example
 *     await setCountry({ countryname: 'Germany', countrycode: 'DE' });
 */
async function setCountry(newCountry) {
    // Check if user is authenticated
    const userAccount = window?.disw?.account?.getAccount();
    // For authenticated users, use their country from Siemens ID profile
    if (userAccount?.authenticated) {
        if (userAccount?.user?.countryCode) {
            try {
                const userCountry = await getFullCountryName(userAccount.user.countryCode);
                _storeLocally(userCountry);
                country = userCountry;
                emit(events.COUNTRY, userCountry, false);
                return;
            }
            catch (error) {
                logger.error(`Error setting country name for logged-in user: ${error}`);
                country = { countryname: '', countrycode: '' };
                emit(events.COUNTRY, undefined, false);
                return;
            }
        }
        else {
            logger.warn('User is authenticated but no country code found in profile');
            country = { countryname: '', countrycode: '' };
            emit(events.COUNTRY, undefined, false);
            return;
        }
    }
    // For non-authenticated users or if fetching authenticated user's country failed
    if (newCountry?.countryname) {
        _storeLocally(newCountry);
        country = newCountry;
        emit(events.COUNTRY, newCountry, false);
    }
    else {
        emit(events.COUNTRY, undefined, false);
    }
}
/**
 * Store Country in localstorage
 *
 * @param {Country} countryData An object consisting countryname and countrycode
 * @example
 *     storeLocally({countryname: 'United States', countrycode: 'US'})
 */
function _storeLocally(countryData) {
    // Store value as a string
    setCookie(COUNTRY_STORAGE_KEY, JSON.stringify(countryData), {
        maxAge: COOKIE_MAX_AGE,
        domain: countryCookieDomain,
        path: COOKIE_DEFAULT_PATH,
        // Only sends the cookie with requests from the cookie's origin site. Server doesn't use this cookie so don't send any more than necessary
        sameSite: COOKIE_SAMESITE_STRICT,
    });
}
/**
 * Get the Country object from localStorage.
 *
 * @returns {Country|null} Returns null of nothing stored locally
 */
function _getLocally() {
    const countryString = getCookieValue(COUNTRY_STORAGE_KEY);
    if (countryString) {
        // Get a JSON object from the stored string
        return JSON.parse(countryString);
    }
    // Otherwise, will return null
    return countryString;
}
/**
 * Function to subscribe to any changes in the country.
 *
 * @param {countryCallback} callback
 * @return {function} function used to unsubscribe from the country events
 *
 * @example
 *     subscribeToCountry(callback)
 */
function subscribeToCountry(callback) {
    return subscribe(events.COUNTRY, callback);
}
/**
 * This is a function to return the current country object
 *
 * @return {Object} country - An object containing current country code and country name
 *
 *  @example
 *
 *     getCountry()
 */
export function getCountry() {
    return country;
}
/**
 * Returns the current country API environment string (e.g. 'dev', 'uat', 'prod').
 * The country module is always initialized before the cart module, so this value
 * is available earlier than the equivalent value from the cart module and can be
 * used as a reliable fallback when the cart module has not yet been initialized.
 *
 * @returns {string} The current country environment.
 *
 * @example
 *     getCountryEnv() // 'dev'
 */
export function getCountryEnv() {
    return countryEnv;
}
export default {
    getAllCountries,
    setCountry,
    subscribeToCountry,
    getCountry,
    getCountryEnv,
};
